
import React from 'react';
import { AppConfig, User, AppMode } from '../../types';
import { TRANSLATIONS } from '../../constants';

interface AppHeaderProps {
  config: AppConfig;
  user: User | null;
  usageCount: number;
  messageLimit: number;
  onSidebarOpen: () => void;
  onNavOpen: () => void;
  onSettingsOpen: () => void;
  onSwitchMode: (mode: AppMode) => void;
  onResetChat: () => void;
  onOpenCommand: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  config, user, usageCount, messageLimit,
  onSidebarOpen, onNavOpen, onSettingsOpen, onSwitchMode, onResetChat, onOpenCommand
}) => {
  const t = TRANSLATIONS[config.language];

  // Don't render header in full-screen modes
  if (['robot', 'profile', 'integrations', 'docs'].includes(config.mode)) {
    return null;
  }

  return (
    <header className="h-16 lg:h-20 border-b border-slate-100 dark:border-white/5 flex items-center justify-between px-4 lg:px-10 bg-white/90 dark:bg-[#0f0f13]/90 backdrop-blur-2xl z-20 shrink-0">
        <div className="flex items-center gap-3 lg:gap-5">
            {(config.mode === 'chat' || config.mode === 'live') && (
            <button onClick={onSidebarOpen} className="lg:hidden w-10 h-10 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl transition-all">
                <i className="fas fa-bars-staggered text-lg"></i>
            </button>
            )}
            <div className="flex items-center gap-3 group cursor-pointer" onClick={onResetChat}>
            <div className="hidden xs:block">
                <h1 className="text-sm lg:text-base font-black text-slate-800 dark:text-white">{t.appName}</h1>
                <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-[9px] font-black text-emerald-500 uppercase tracking-[0.2em]">{t.activeAI}</span>
                </div>
            </div>
            </div>
        </div>

        {/* Desktop Search / Command Trigger */}
        <div className="hidden md:flex flex-1 max-w-md mx-6">
           <button 
             onClick={onOpenCommand}
             className="w-full h-11 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200/50 dark:border-white/5 rounded-2xl px-4 flex items-center justify-between text-slate-400 transition-all group"
           >
              <div className="flex items-center gap-3">
                 <i className="fas fa-terminal text-sm group-hover:text-emerald-500 transition-colors"></i>
                 <span className="text-xs font-bold">{config.language === 'ar' ? 'البحث السريع والأوامر...' : 'Search or type command...'}</span>
              </div>
              <div className="flex items-center gap-1 bg-white dark:bg-white/5 px-2 py-0.5 rounded-lg border border-slate-200 dark:border-white/10 shadow-sm">
                 <span className="text-[10px] font-black">⌘K</span>
              </div>
           </button>
        </div>

        <div className="flex items-center bg-slate-100 dark:bg-white/[0.04] p-1 rounded-2xl border border-slate-200/50 dark:border-white/10">
            <button onClick={() => onSwitchMode('chat')} className={`w-10 lg:w-auto lg:px-6 py-2 rounded-[14px] text-[10px] lg:text-[11px] font-black transition-all flex items-center justify-center gap-2.5 ${config.mode === 'chat' ? 'bg-white dark:bg-emerald-500 text-emerald-600 dark:text-white shadow-lg' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>
            <i className="fas fa-message text-[12px]"></i>
            <span className="hidden lg:inline">{t.chatRoom}</span>
            </button>
            <button onClick={() => onSwitchMode('live')} className={`w-10 lg:w-auto lg:px-6 py-2 rounded-[14px] text-[10px] lg:text-[11px] font-black transition-all flex items-center justify-center gap-2.5 ${config.mode === 'live' ? 'bg-white dark:bg-emerald-500 text-emerald-600 dark:text-white shadow-lg' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>
            <i className="fas fa-microphone-lines text-[12px]"></i>
            <span className="hidden lg:inline">{t.liveMode}</span>
            </button>
        </div>

        <div className="flex items-center gap-2 lg:gap-3">
            <button onClick={onOpenCommand} className="md:hidden w-10 h-10 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl transition-all">
                <i className="fas fa-terminal text-lg"></i>
            </button>
            <div className="hidden md:flex items-center gap-2 bg-emerald-500/5 px-3 py-1.5 rounded-xl border border-emerald-500/10">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400">{usageCount}/{user?.plan === 'pro' || user?.plan === 'enterprise' ? '∞' : messageLimit} <span className="text-slate-400 dark:text-slate-600 ml-1">{config.language === 'ar' ? 'رسالة' : 'Msgs'}</span></span>
            </div>
            <button onClick={onSettingsOpen} className="w-10 h-10 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl transition-all">
            <i className="fas fa-gear text-lg lg:text-xl"></i>
            </button>
            <button onClick={onNavOpen} className="lg:hidden w-10 h-10 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl transition-all">
            <i className="fas fa-grip-vertical text-lg"></i>
            </button>
        </div>
    </header>
  );
};
export default AppHeader;
